/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormGroupSchemaConfig,
  FormState,
  FormStateBuilder,
  TextTypes,
  updateSchemaWithChildren,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  collectValues,
  NotificationService,
  WaitingService,
  AutocompleteLoadMoreEvent,
} from '@gh/core-ui';
import {
  JobDetailsTabBusinessService,
  JobEDocOwnerSet,
} from '../../../../resources/job-details-tab';
import { ContactCustomerListItemDto } from '@gh/builder-shared-data';
@Component({
  standalone: false,
  selector: 'gh-job-details-e-docs-owner-set-form',
  templateUrl: 'job-details-e-docs-owner-set-form.component.html',
})
export class JobDetailsEDocsOwnerSetFormComponent implements OnInit {
  @Input() editable: boolean;
  @Input() jobId: ID;
  @Input() owners: JobEDocOwnerSet;

  @Output() start = new EventEmitter<void>();
  @Output() save = new EventEmitter<JobEDocOwnerSet>();
  @Output() cancel = new EventEmitter<void>();

  form: FormState<JobEDocOwnerSet>;

  inEditing = false;

  private originalOwnerSet: JobEDocOwnerSet;

  constructor(private formStateBuilder: FormStateBuilder,
              private waitings: WaitingService,
              private notifications: NotificationService,
              private business: JobDetailsTabBusinessService) {
  }

  ngOnInit(): void {
    this.form = this.formStateBuilder.buildPlain({
      value: this.owners,
      optimisticLock: true,
      ...this.getFormSchemaConfig(this.owners),
    });
  }

  onStartEdit(): void {
    this.originalOwnerSet = collectValues(this.form.group);

    this.start.emit();
    this.inEditing = true;
    this.onUpdateForm();
  }

  onUpdateForm(): void {
    updateSchemaWithChildren(this.form.group, this.getFormSchemaConfig(this.form.value));
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.business.updateEdocOwnerSet(this.jobId, this.form.submitValue).pipe(
      this.waitings.operator())
      .subscribe(
        (ownerSet) => {
          this.form.setValue(ownerSet);
          this.notifications.success('msg_saved_successfully');
          this.inEditing = false;
          this.form.group.markAsPristine();
          this.onUpdateForm();
          this.save.emit(ownerSet);
        },
        this.notifications.processAll);
  }

  onCancel(): void {
    this.form.setValue(this.originalOwnerSet);
    this.inEditing = false;
    this.form.group.markAsPristine();
    this.onUpdateForm();
    this.cancel.emit();
  }

onOwnerSelected(event?: ContactCustomerListItemDto): void {
  if(!event) return;
  this.form.getControl("owner1").setValue(event?.name);
  const customerEmail = event?.email;
  if(customerEmail){
    this.form.getControl("email1").setValue(customerEmail);
    this.form.getControl("signer1").setValue(true);
  }
}

loadContacts(event: AutocompleteLoadMoreEvent): void {
  this.business.findCustomersByName(event.query).subscribe((response: any[]) => event.load(response));
}

  private getFormSchemaConfig(owners: JobEDocOwnerSet): FormGroupSchemaConfig {
    return {
      enabled: this.inEditing,
      children: {
        owner1: { required: true },
        signer1: void 0,
        email1: { required: owners.signer1, type: TextTypes.Email },
        owner2: { required: owners.signer2 },
        signer2: void 0,
        email2: { required: owners.signer2, type: TextTypes.Email },
        owner3: { required: owners.signer3 },
        signer3: void 0,
        email3: { required: owners.signer3, type: TextTypes.Email },
      },
    };
  }
}
